import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface EmailTemplate {
  id: string;
  template_key: string;
  name: string;
  description: string | null;
  html_content: string;
  subject_template: string | null;
  supabase_type: string;
  category: string;
  variables: string[];
  version: number;
  is_active: boolean;
  is_draft: boolean;
  created_at: string;
  updated_at: string;
  published_at: string | null;
  config_url: string | null;
}

export const useEmailTemplates = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: templates, isLoading, error } = useQuery({
    queryKey: ['email-templates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .order('template_key', { ascending: true })
        .order('version', { ascending: false });

      if (error) throw error;
      return data as EmailTemplate[];
    },
    staleTime: 1000 * 60 * 5,
  });

  const updateTemplate = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<EmailTemplate> }) => {
      const { data, error } = await supabase
        .from('email_templates')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      toast({
        title: 'Template atualizado',
        description: 'Alterações salvas com sucesso',
      });
    },
  });

  const publishTemplate = useMutation({
    mutationFn: async (id: string) => {
      const { data: template } = await supabase
        .from('email_templates')
        .select('template_key')
        .eq('id', id)
        .single();

      if (template) {
        await supabase
          .from('email_templates')
          .update({ is_active: false })
          .eq('template_key', template.template_key);
      }

      const { data, error } = await supabase
        .from('email_templates')
        .update({
          is_active: true,
          is_draft: false,
          published_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      toast({
        title: 'Template publicado',
        description: 'O template está agora ativo',
      });
    },
  });

  const createNewVersion = useMutation({
    mutationFn: async (templateId: string) => {
      const { data: original, error: fetchError } = await supabase
        .from('email_templates')
        .select('*')
        .eq('id', templateId)
        .single();

      if (fetchError || !original) throw fetchError;

      const { data, error } = await supabase
        .from('email_templates')
        .insert([{
          template_key: original.template_key,
          name: original.name,
          description: original.description,
          html_content: original.html_content,
          supabase_type: original.supabase_type,
          category: original.category,
          variables: original.variables,
          version: original.version + 1,
          is_draft: true,
          is_active: false,
          config_url: original.config_url,
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      toast({
        title: 'Nova versão criada',
        description: 'Você pode editar a nova versão',
      });
    },
  });

  return {
    templates,
    isLoading,
    error,
    updateTemplate,
    publishTemplate,
    createNewVersion,
  };
};
